import type { ReactNode } from 'react';

/**
 * Shared primitives for the /compare/* dossier pages. The pages run grid-free
 * (GridOff) on the handoff's own 1224px/36px container — deliberately NOT
 * SectionShell's 1200px/px-7 band (same decision as /about and /qr-codes;
 * own copy so the pages stay independently editable).
 */
export const CONTAINER = 'mx-auto w-full max-w-[1224px] px-6 md:px-9';

/** Section H2 — onest 800, the dossier register's heading voice. */
export const H2 =
  'mt-4 font-onest text-[clamp(28px,3.2vw,36px)] font-extrabold leading-[1.12] tracking-[-0.02em]';

/** Two-tone mono section number — `01 — THE VERDICT`. */
export function SectionTag({
  num,
  label,
  tone = 'ink',
}: {
  num: string;
  label: string;
  tone?: 'ink' | 'light';
}) {
  return (
    <span className="block font-jetbrains text-[11px] font-semibold tracking-[0.16em]">
      <span className={tone === 'light' ? 'text-[#ff9ec0]' : 'text-brand-600'}>{num}</span>
      <span className={tone === 'light' ? 'text-white/45' : 'text-dim'}> — {label}</span>
    </span>
  );
}

/** All-caps mono caption under a section object. */
export function MonoCaption({
  children,
  className = '',
  ...rest
}: {
  children: ReactNode;
  className?: string;
} & React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      {...rest}
      className={`font-jetbrains text-[10px] leading-[1.6] tracking-[0.12em] text-dim ${className}`}
    >
      {children}
    </p>
  );
}

/** Hero / table-head verification pill. `dot` renders the green verified dot. */
export function Chip({
  children,
  dot = false,
  className = '',
}: {
  children: ReactNode;
  dot?: boolean;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-[7px] rounded-full border border-line-3 bg-surface px-3 py-1.5 font-jetbrains text-[10.5px] tracking-[0.08em] ${
        dot ? 'text-ink' : 'text-muted'
      } ${className}`}
    >
      {dot && (
        <span className="size-1.5 flex-none animate-status-pulse rounded-full bg-data-green" />
      )}
      {children}
    </span>
  );
}

/** Small square-cornered data stamp — amber (caution) or green (relief). */
export function Stamp({
  tone,
  children,
  className = '',
}: {
  tone: 'amber' | 'green';
  children: ReactNode;
  className?: string;
}) {
  const tint =
    tone === 'amber'
      ? 'bg-[color-mix(in_srgb,var(--color-data-amber)_12%,white)] border-[color-mix(in_srgb,var(--color-data-amber)_40%,transparent)]'
      : 'bg-[color-mix(in_srgb,var(--color-data-green)_12%,white)] border-[color-mix(in_srgb,var(--color-data-green)_40%,transparent)]';
  return (
    <span
      className={`inline-block rounded-[4px] border px-2 py-0.5 font-jetbrains text-[9.5px] tracking-[0.08em] text-ink ${tint} ${className}`}
    >
      {children}
    </span>
  );
}

/** Verdict/ledger column pill — brand-tinted (rii) or neutral (competitor). */
export function ColumnPill({
  tone,
  children,
}: {
  tone: 'brand' | 'neutral';
  children: ReactNode;
}) {
  return (
    <span
      className={`self-start rounded-full px-2.5 py-[3px] font-jetbrains text-[10px] tracking-[0.1em] ${
        tone === 'brand'
          ? 'border border-[color-mix(in_srgb,var(--color-brand-600)_35%,transparent)] bg-[color-mix(in_srgb,var(--color-brand-600)_8%,white)] text-brand-700'
          : 'border border-line-3 text-muted'
      }`}
    >
      {children}
    </span>
  );
}
